"use client";

import { useCallback, useMemo, useState, useEffect } from "react";
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import type { ISourceOptions } from "@tsparticles/engine";

interface ParticlesBackgroundProps {
  variant?: "hero" | "light" | "minimal";
  className?: string;
}

export function ParticlesBackground({ 
  variant = "hero", 
  className = "" 
}: ParticlesBackgroundProps) {
  const [init, setInit] = useState(false);

  useEffect(() => {
    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      setInit(true);
    });
  }, []);

  const options: ISourceOptions = useMemo(() => {
    const baseConfig: ISourceOptions = {
      fullScreen: false,
      fpsLimit: 60,
      detectRetina: true,
      particles: {
        number: {
          value: variant === "hero" ? 50 : variant === "light" ? 30 : 20,
          density: {
            enable: true,
          },
        },
        color: {
          value: ["#ffffff", "#f59e0b", "#fbbf24"],
        },
        shape: {
          type: "circle",
        },
        opacity: {
          value: { min: 0.1, max: 0.5 },
          animation: {
            enable: true,
            speed: 0.5,
            sync: false,
          },
        },
        size: {
          value: { min: 1, max: 3 },
          animation: {
            enable: true,
            speed: 2,
            sync: false,
          },
        },
        links: {
          enable: true,
          distance: 120,
          color: "#ffffff",
          opacity: 0.15,
          width: 1,
        },
        move: {
          enable: true,
          speed: 1,
          direction: "none",
          random: true,
          straight: false,
          outModes: {
            default: "bounce",
          },
        },
      },
      interactivity: {
        detectsOn: "canvas",
        events: {
          onHover: {
            enable: true,
            mode: "grab",
          },
          onClick: {
            enable: true,
            mode: "push",
          },
        },
        modes: {
          grab: {
            distance: 140,
            links: {
              opacity: 0.4,
              color: "#f59e0b",
            },
          },
          push: {
            quantity: 3,
          },
        },
      },
    };

    // Add sparks effect for hero variant
    if (variant === "hero") {
      baseConfig.particles!.number!.value = 60;
    }

    return baseConfig;
  }, [variant]);

  if (!init) return null;

  return (
    <Particles
      id={`particles-${variant}`}
      className={`absolute inset-0 ${className}`}
      options={options}
    />
  );
}
